import {useState, useCallback, useEffect, useMemo, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {getTutorialSteps} from '../tutorialSteps'
import {START_TOUR_EVENT} from '../startTour'
import {useMarkTutorialAsCompleted} from '../../user-tutorial/hooks/useMarkTutorialAsCompleted'
import {useAuth} from '../../auth'

interface Step {
  element: string
  title: string
  intro: string
}

const MAX_LAYOUT_WAIT_MS = 8000
const LAYOUT_POLL_MS = 60

const isUsable = (el: HTMLElement | null): boolean => {
  if (!el) return false
  const style = window.getComputedStyle(el)
  const rect = el.getBoundingClientRect()
  return (
    style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0
  )
}

// Só mostra passo cujo alvo existe e está visível nesta tela (desktop ou mobile, papel do usuário).
const keepUsableSteps = (steps: Step[]): Step[] =>
  steps.filter((step) => isUsable(document.querySelector(step.element) as HTMLElement | null))

// Prefixa o contador de passos no texto (o Intro.js renderiza `intro` como HTML).
const withCounter = (steps: Step[]): Step[] =>
  steps.map((step, index) => ({
    ...step,
    intro: `<div class="tr-tour-counter">Passo ${index + 1} de ${steps.length}</div>${step.intro}`,
  }))

export const useTutorial = () => {
  const location = useLocation()
  const [activeTutorial, setActiveTutorial] = useState<string>()
  const {currentUser} = useAuth()
  const [stepsEnabled, setStepsEnabled] = useState(false)
  const {mutateAsync: markTutorialAsCompletedFn} = useMarkTutorialAsCompleted()
  const [steps, setSteps] = useState<Step[]>([])
  // Reprise pedida pelo usuário (menu de ajuda): percorre os tutoriais sem marcar nada como visto.
  const [replay, setReplay] = useState(false)

  useEffect(() => {
    if (replay) return
    if (!currentUser) return

    const incompletedTutorial = currentUser.tutorials?.find((tutorial) => !tutorial.is_completed)
    if (incompletedTutorial) {
      setActiveTutorial(incompletedTutorial.name)
    } else {
      setActiveTutorial(undefined)
      setStepsEnabled(false)
    }
  }, [currentUser, replay])

  useEffect(() => {
    const restart = () => {
      setStepsEnabled(false)
      setSteps([])
      setReplay(true)
      setActiveTutorial('navbar')
    }
    window.addEventListener(START_TOUR_EVENT, restart)
    return () => window.removeEventListener(START_TOUR_EVENT, restart)
  }, [])

  const candidateSteps = useMemo(
    () => getTutorialSteps(activeTutorial, currentUser),
    // Depende só do papel, não do objeto inteiro (que muda a cada atualização do usuário).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTutorial, currentUser?.administrator, currentUser?.manager, currentUser?.external_user]
  )

  const handleExit = useCallback(async () => {
    if (!activeTutorial) return

    setStepsEnabled(false)

    if (replay) {
      if (activeTutorial === 'navbar') {
        setActiveTutorial('sidebar')
      } else {
        setActiveTutorial(undefined)
        setReplay(false)
      }
      return
    }

    const tutorialToMark = currentUser?.tutorials?.find(
      (tutorial) => tutorial.name === activeTutorial
    )

    if (tutorialToMark) {
      await markTutorialAsCompletedFn({
        userId: currentUser?.id!,
        tutorialId: tutorialToMark.id,
      })
    }

    const nextTutorial = currentUser?.tutorials?.find(
      (tutorial) => tutorial.name !== activeTutorial && !tutorial.is_completed
    )

    if (nextTutorial) {
      setActiveTutorial(nextTutorial.name)
    } else {
      setStepsEnabled(false)
    }
  }, [activeTutorial, currentUser, markTutorialAsCompletedFn, replay])

  // O efeito abaixo não pode depender do `handleExit` (ele muda a cada atualização do usuário e
  // reiniciaria a espera), então chamamos sempre a versão mais recente por ref.
  const handleExitRef = useRef(handleExit)
  useEffect(() => {
    handleExitRef.current = handleExit
  }, [handleExit])

  /**
   * O Intro.js dispara antes de os alvos existirem (layout ainda montando, principalmente logo
   * depois de sair de /onboarding). Esperamos o conjunto de alvos visíveis se estabilizar, filtramos
   * o que não existe nesta tela e só então ligamos o tour. Sem nenhum alvo, o tutorial é encerrado.
   */
  useEffect(() => {
    setStepsEnabled(false)
    setSteps([])

    if (!activeTutorial || candidateSteps.length === 0) return
    if (!replay && !currentUser?.onboarded) return
    if (location.pathname.startsWith('/onboarding')) return

    let cancelled = false
    let timer: number | undefined
    let lastCount = -1
    const startedAt = Date.now()

    const tick = () => {
      if (cancelled) return

      const usable = keepUsableSteps(candidateSteps)
      const timedOut = Date.now() - startedAt > MAX_LAYOUT_WAIT_MS
      const stable = usable.length > 0 && usable.length === lastCount

      if (stable || timedOut) {
        if (usable.length === 0) {
          void handleExitRef.current()
          return
        }
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (cancelled) return
            setSteps(withCounter(usable))
            setStepsEnabled(true)
          })
        })
        return
      }

      lastCount = usable.length
      timer = window.setTimeout(tick, LAYOUT_POLL_MS)
    }

    timer = window.setTimeout(tick, 160)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [activeTutorial, candidateSteps, location.pathname, currentUser?.onboarded, replay])

  return {
    stepsEnabled,
    steps,
    setStepsEnabled,
    activeTutorial,
    handleExit,
  }
}
